/**
 * ONE ADDRESS PER PAGE, decided in exactly one place.
 *
 * Pure and browser-safe. Every canonical tag, every `og:url` and every absolute
 * asset URL in the site resolves through here, for one reason: a page that is
 * reachable at more than one origin and does not say which one is the real one
 * gets its ranking signals split between them, and a share preview whose image
 * URL is relative simply does not render.
 *
 * THE PROBLEM THIS SOLVES. Canonicals used to be built from `getBaseUrl(req)`,
 * which reconstructs the origin from the request's own `Host` header. That is
 * right for a preview deployment and wrong for production: the apex
 * `bazio.app` and `www.bazio.app` both reach the site (the apex is redirected
 * in `next.config.ts`, but a crawler that followed a stale apex link still
 * rendered a page whose canonical named the apex), so the same listing could
 * present two different canonical URLs depending on which host the crawler
 * happened to ask.
 *
 * THE RULE. A request arriving on ANY host that belongs to the production site
 * canonicalises to `CANONICAL_ORIGIN`. A request arriving anywhere else — a
 * preview host, localhost — keeps its own origin, because a preview that
 * canonicalised to production would be claiming to BE production.
 */

/**
 * The one public origin. `www`, because that is the host the certificate, the
 * redirect in `next.config.ts` and the Telegram webhook all agree on.
 *
 * `@/lib/telegram/platform-webhook` and `@/lib/auth/session-policy` each hold
 * their own copy of this host on purpose: the webhook must not be able to break
 * because a metadata module moved, and the cookie domain is a security control
 * rather than a presentation one. They must all name the same host, and
 * `canonical.test.ts` asserts exactly that.
 */
export const CANONICAL_ORIGIN = 'https://www.bazio.app'

/**
 * Every host that IS the production site. Both collapse to `CANONICAL_ORIGIN`,
 * which is what stops the apex and the `www` host indexing as two sites.
 */
const PRODUCTION_HOSTS = new Set(['bazio.app', 'www.bazio.app'])

const hostOf = (origin: string): string | null => {
    try {
        return new URL(origin).host.toLowerCase()
    } catch {
        return null
    }
}

/**
 * The origin this page should present itself at.
 *
 * Given nothing at all it answers the canonical origin rather than an empty
 * string, because a metadata layer that emits a relative `og:image` is worse
 * than one that occasionally names production during local development.
 */
export const canonicalOrigin = (baseUrl?: string | null): string => {
    const trimmed = baseUrl?.trim().replace(/\/+$/, '')

    if (!trimmed) return CANONICAL_ORIGIN

    const host = hostOf(trimmed)

    if (!host) return CANONICAL_ORIGIN

    return PRODUCTION_HOSTS.has(host) ? CANONICAL_ORIGIN : trimmed
}

/** True when this origin is the real site, so its pages may be indexed. */
export const isProductionOrigin = (baseUrl?: string | null): boolean => {
    const host = hostOf(canonicalOrigin(baseUrl))

    return host !== null && PRODUCTION_HOSTS.has(host)
}

/**
 * A path, normalised to the ONE form the site links to.
 *
 * Trailing slashes, duplicated slashes and a query string are all removed: a
 * canonical URL names the document, and `?category=phones&page=2` is a view of
 * it. `/browse` filtered by a query string carries `noindex` for the same
 * reason, decided at the page rather than here.
 */
export const canonicalPath = (path: string): string => {
    const [withoutQuery] = (path || '/').split(/[?#]/)
    const collapsed = `/${withoutQuery}`.replace(/\/{2,}/g, '/')

    if (collapsed === '/') return '/'

    return collapsed.replace(/\/+$/, '')
}

/** The absolute canonical URL for a path on this site. */
export const canonicalUrl = (baseUrl: string | null | undefined, path: string): string => {
    const origin = canonicalOrigin(baseUrl)
    const suffix = canonicalPath(path)

    return suffix === '/' ? origin : `${origin}${suffix}`
}

/**
 * Absolutises anything that will be read by a CRAWLER rather than a browser.
 *
 * An already-absolute URL (a listing photo on the CDN) is returned untouched.
 * A protocol-relative URL is given https, because a crawler is not a browser
 * and does not always have a scheme to inherit.
 */
export const toAbsoluteUrl = (baseUrl: string | null | undefined, url: string): string => {
    const value = url.trim()

    if (/^https?:\/\//i.test(value)) return value
    if (value.startsWith('//')) return `https:${value}`

    return `${canonicalOrigin(baseUrl)}${value.startsWith('/') ? value : `/${value}`}`
}
